import { maintenanceTemplateDeleteBlock, type MaintenanceTemplate } from '@gb-ticket/shared-types';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ApiRequestError } from '@/lib/api';
import { useDeleteMaintenanceTemplate } from '@/products/maintenance/features/use-maintenance-templates';

export function MaintenanceTemplateDeleteButton({
  template,
  onDeleted,
}: {
  template: Pick<MaintenanceTemplate, 'id' | 'name' | 'usageCount'>;
  onDeleted?: () => void;
}) {
  const block = maintenanceTemplateDeleteBlock(template.usageCount);
  const remove = useDeleteMaintenanceTemplate();
  const [open, setOpen] = useState(false);

  async function confirmDelete() {
    if (block) {
      return;
    }
    try {
      await remove.mutateAsync(template.id);
      toast.success('Vorlage gelöscht.');
      setOpen(false);
      onDeleted?.();
    } catch (err: unknown) {
      const message =
        err instanceof ApiRequestError ? err.message : 'Die Vorlage konnte nicht gelöscht werden.';
      toast.error(message);
    }
  }

  return (
    <>
      <Button type="button" variant="outline" className="min-h-11" onClick={() => setOpen(true)}>
        Löschen
      </Button>
      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            setOpen(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {block ? 'Löschen nicht möglich' : `Vorlage „${template.name}“ löschen?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {block ??
                'Die Vorlage und ihre ungenutzten Versionen werden entfernt. Abbrechen lässt den Datensatz stehen.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>
              {block ? 'Schließen' : 'Abbrechen'}
            </AlertDialogCancel>
            {block ? null : (
              <AlertDialogAction
                disabled={remove.isPending}
                onClick={(event) => {
                  event.preventDefault();
                  void confirmDelete();
                }}
              >
                Löschen
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
